import type { MicDevice } from "./micDevices";
import type { MicPlatform } from "./micPlatform";

export type MicStateId = "R10" | "R12" | "R13" | "R14" | "R14W" | "R31" | "R16" | "R17" | "error";
export type MicPermission = "granted" | "prompt" | "denied" | "unknown";

export const MIC_BLOCKING_STATES: ReadonlySet<MicStateId> = new Set<MicStateId>([
    "R13",
    "R14",
    "R14W",
    "R31",
    "R16",
    "R17",
]);

// inputs is null when enumerateDevices failed: the device list is unknown, so it never means R16.
export const idleMicState = (a: {
    permission: MicPermission;
    inputs: MicDevice[] | null;
    platform: MicPlatform;
}): MicStateId => {
    // Some browsers hide devices until access is granted, so an empty list means "no mic" only once
    // permission is granted. Before that it stays R12 and a record press lets getUserMedia decide.
    if (a.permission === "granted" && a.inputs && a.inputs.length === 0) return "R16";
    if (a.permission === "denied") return a.platform.os === "ios" ? "R31" : "R13";
    if (a.permission === "granted") return "R10";
    // No permissions API answer: labels are only exposed once access was granted.
    if (a.permission === "unknown" && a.inputs?.some((d) => d.label !== "")) return "R10";
    return "R12";
};

// Classifies by err.name only: messages are localized and differ per browser.
export const classifyGetUserMediaError = (
    err: unknown,
    permission: MicPermission,
    platform: MicPlatform,
): MicStateId => {
    const name = err && typeof err === "object" ? (err as { name?: unknown }).name : undefined;
    switch (name) {
        case "NotAllowedError":
        case "SecurityError":
            if (platform.os === "ios") return "R31";
            if (permission === "denied") return "R13";
            if (permission === "granted") {
                if (platform.os === "mac") return "R14";
                if (platform.os === "windows") return "R14W";
            }
            return "R13";
        case "NotReadableError":
        case "AbortError":
            return platform.os === "windows" ? "R14W" : "R17";
        case "NotFoundError":
        case "OverconstrainedError":
            return "R16";
        default:
            return "error";
    }
};

export class MicStartError extends Error {
    readonly cause: unknown;
    constructor(message: string, cause: unknown) {
        super(message);
        // Target is ES5, so restore the prototype for instanceof to work.
        Object.setPrototypeOf(this, MicStartError.prototype);
        this.name = "MicStartError";
        this.cause = cause;
    }
}
